import React, { useEffect, useRef, useState } from "react";
import "./Sidebar.css";
import Modal from "./Modal";
import axios from "axios";
import {
  logout,
  getToken,
  getActiveChatId,
  setActiveChatId,
} from "../utils/session";

import { API_BASE } from "../utils/apiConfig";

function Sidebar({ collapsed, toggleSidebar, onChatSelect }) {
  const [chatList, setChatList] = useState([]);
  const [activeChat, setActiveChat] = useState(getActiveChatId());
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(null);

  const userRef = useRef(null);
  const token = getToken();

  /* =========================
   LISTEN FOR TITLE UPDATES
========================= */
    useEffect(() => {
      const updateTitle = (e) => {
        const { chatId: updatedId, title } = e.detail;

        setChatList((prev) =>
          prev.map((chat) =>
            chat._id === updatedId ? { ...chat, title } : chat
          )
        );
      };
      
      const onChatCreated = (e) => {
        const newChat = e.detail;
        setChatList((prev) => [newChat, ...prev]);
        setActiveChat(newChat._id);
        setActiveChatId(newChat._id);
        // onChatSelect is already handled by ChatPage logic
      };

      window.addEventListener("chatTitleUpdated", updateTitle);
      window.addEventListener("chatCreated", onChatCreated);

      return () => {
        window.removeEventListener("chatTitleUpdated", updateTitle);
        window.removeEventListener("chatCreated", onChatCreated);
      };
    }, []);


  /* =========================
     LOAD USER
  ========================= */
  useEffect(() => {
    if (!token) return;

    axios
      .get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("logicline_user_name", res.data.name);
      })
      .catch(() => {
        logout();
        window.location.reload();
      });
  }, [token]);

  /* =========================
     LOAD CHATS
  ========================= */
  useEffect(() => {
    if (!token) return;

    axios
      .get(`${API_BASE}/chats`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setChatList(res.data);
      })
      .catch((err) => {
        console.error("Failed to load chats", err);
      });
  }, [token]);

  /* =========================
     OUTSIDE CLICK CLOSE
  ========================= */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* =========================
     CREATE CHAT
  ========================= */
  const createNewChat = async () => {
    try {
      const res = await axios.post(
        `${API_BASE}/chats`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newChat = res.data;

      setChatList((prev) => [newChat, ...prev]);
      setActiveChat(newChat._id);
      setActiveChatId(newChat._id);

      onChatSelect(newChat._id); // <<< FIX: update ChatPage too
    } catch (err) {
      console.error("Failed to create chat", err);
    }
  };

  /* =========================
     DELETE CHAT
  ========================= */
  const [deleteId, setDeleteId] = useState(null);

  const confirmDelete = async () => {
    if (!deleteId) return;
    const chatId = deleteId;
    setDeleteId(null);

    try {
      await axios.delete(`${API_BASE}/chats/${chatId}`, {
         headers: { Authorization: `Bearer ${token}` },
      });

      setChatList((prev) => prev.filter((c) => c._id !== chatId));

      // if deleted chat was active → clear it
      if (chatId === activeChat) {
        setActiveChat(null);
        setActiveChatId(null);
        onChatSelect(null);
      }
    } catch (err) {
      console.error("Failed to delete chat", err);
    }
  };

  const handleDeleteClick = (chatId, e) => {
    e.stopPropagation();
    setDeleteId(chatId);
  };


  /* =========================
     OPEN CHAT (MAIN FIX)
  ========================= */
  const openChat = (chatId) => {
    setActiveChat(chatId);
    setActiveChatId(chatId);
    onChatSelect(chatId); // <<< FIX: trigger ChatPage reload
  };

  /* =========================
     LOGOUT
  ========================= */
  const handleLogout = () => {
    logout();
    localStorage.removeItem("logicline_user_name");
    window.location.reload();
  };

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  return (
    <>
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`} aria-label="Chat history">
      {/* ================= BRAND ================= */}
      {!collapsed && (
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src="/logo192.png" alt="" className="brand-logo" />
            <span className="brand-name">LogicLine</span>
          </div>
          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={toggleSidebar}
            aria-label="Close chat history"
            title="Close chat history"
          >
            <span className="sidebar-toggle-glyph" aria-hidden="true"><span /></span>
          </button>
        </div>
      )}

      {!collapsed && (
        <>
          {/* ================= NEW CHAT ================= */}
          <button className="new-chat-btn" onClick={createNewChat}>
            + New chat
          </button>

          {/* ================= CHAT LIST ================= */}
          <div className="chat-history">
            {chatList.map((chat) => (
              <div
                  key={chat._id}
                  className={`chat-item ${
                    chat._id === activeChat ? "active" : ""
                  }`}
                  onClick={() => openChat(chat._id)}
                >
                  <span className="chat-title">
                    {chat.title || "Untitled chat"}
                  </span>

                  <button
                    className="delete-chat-btn"
                    onClick={(e) => handleDeleteClick(chat._id, e)}
                    title="Delete chat"
                  >
                    🗑
                  </button>
              </div>

            ))}
          </div>
        </>
      )}

      {/* ================= USER FOOTER ================= */}
      {!collapsed && user && (
        <div className="user-menu" ref={userRef}>
          <div
            className="user-row"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <strong>{user.name}</strong>
            </div>
          </div>

          {menuOpen && (
            <div className="user-dropdown">
              <button onClick={handleLogout}>Log out</button>
            </div>
          )}
        </div>
      )}

      <Modal
        title="Delete Chat"
        message="Are you sure you want to delete this chat? This action cannot be undone."
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        actions={
          <>
            <button className="text-btn" onClick={() => setDeleteId(null)}>
              Cancel
            </button>
            <button className="danger-btn" onClick={confirmDelete}>
              Delete
            </button>
          </>
        }
      />
    </aside>
    {collapsed && (
      <button
        type="button"
        className="sidebar-reopen-btn"
        onClick={toggleSidebar}
        aria-label="Open chat history"
        title="Open chat history"
      >
        <span className="sidebar-toggle-glyph" aria-hidden="true"><span /></span>
      </button>
    )}
    </>
  );
}

export default Sidebar;
